import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import * as actions from './company.actions';
import * as CompanySelector from './company.selectors';
import { CompanyNgrxService } from '../company.service';
import { AuthService, Company, PowerUser } from '@ridango/core';
import { map } from 'rxjs/operators';

@Injectable()
export class CompanyFacade {
    allCompanies$: Observable<Company[]> = this.store.select(CompanySelector.getAllCompanies);
    selectedCompany$: Observable<Company> = this.store.select(CompanySelector.getSelectedCompany);
    powerUsers$: Observable<PowerUser[]> = this.store.select(CompanySelector.getPowerUsers);
    loaded$: Observable<boolean> = this.store.select(CompanySelector.getLoadedStatus);

    companiesOnly$ = this.allCompanies$.pipe(
        map((companies) => companies.filter((company) => !company.parentCompany || !company.parentCompany.id))
    );

    departmentsOnly$: Observable<Company[]> = this.allCompanies$.pipe(
        map((companies) =>
            companies.filter((company) => company.parentCompany && Object.keys(company.parentCompany).length > 0)
        )
    );

    filteredDepartments$ = combineLatest([this.selectedCompany$, this.departmentsOnly$, this.companiesOnly$]).pipe(
        map(([selected, departments, companies]) => {
            if (companies.length === 0) {
                return departments;
            }

            const companiesIds = companies.map((c) => c.id);
            const departmentWithoutCompany = departments.filter(
                (dept) => !dept.parentCompany?.id || !companiesIds.includes(dept.parentCompany.id)
            );

            if (!selected) {
                return departmentWithoutCompany;
            }

            const parentId = selected.parentCompany?.id ? selected.parentCompany.id : selected.id;
            const tiedDepartments = departments.filter((dept) => dept.parentCompany?.id === parentId);

            const combined = [...tiedDepartments, ...departmentWithoutCompany];
            return combined.filter((dept, index, self) => index === self.findIndex((d) => d.id === dept.id));
        })
    );

    constructor(
        private store: Store,
        private authService: AuthService,
        private service: CompanyNgrxService
    ) {}

    fetchAllCompanies(): void {
        this.store.dispatch(actions.fetchAllCompanies());
    }

    setAllCompanies(companies: Company[]) {
        this.store.dispatch(actions.setAllCompanies({ companies }));
    }

    setSelectedCompany(company: Company) {
        if (company) {
            this.authService.setLoggedInUserCompanyId(company.id);
        }
        this.store.dispatch(actions.setSelectedCompany({ company }));
    }

    saveCompanyProfile(company: Company) {
        this.store.dispatch(actions.saveCompanyProfile({ company }));
    }

    fetchCompany(companyId: number) {
        return this.service.fetchCompany(companyId);
    }

    fetchPowerUsers(companyId: number) {
        this.store.dispatch(actions.fetchPowerUsers({ companyId }));
    }

    deletePowerUser(companyId: number, userId: number) {
        this.store.dispatch(actions.deletePowerUser({ companyId, userId }));
    }

    addPowerUser(powerUser: PowerUser, companyId: number) {
        this.store.dispatch(actions.addPowerUser({ powerUser, companyId }));
    }
}
